<template>
  <Panel header="Danh sách văn bản">
    <div class="w-full">
      <div class="mb-4 rounded-lg border border-slate-200 bg-white p-4 shadow-[0_10px_26px_rgba(15,23,42,0.05)]">
        <div class="mb-4 flex flex-wrap gap-2">
          <Button
            type="button"
            :label="`Chưa trả lời (${pendingCount})`"
            icon="pi pi-hourglass"
            :outlined="activeTab !== 'pending'"
            severity="danger"
            @click="activeTab = 'pending'"
          />
          <Button
            type="button"
            :label="`Đã trả lời (${answeredCount})`"
            icon="pi pi-check"
            :outlined="activeTab !== 'answered'"
            severity="success"
            @click="activeTab = 'answered'"
          />
        </div>

        <div class="grid grid-cols-1 gap-3 lg:grid-cols-[180px_180px_minmax(260px,1fr)_auto]">
          <div class="flex min-w-0 flex-col gap-1.5">
            <label class="text-sm font-bold text-slate-600">Từ thời gian</label>
            <div class="date-picker">
              <button
                type="button"
                class="date-picker-input"
                @click="toggleDatePicker('from')"
              >
                <span :class="{ 'date-picker-placeholder': !dateFrom }">{{ formatDatePickerValue(dateFrom) || "dd/mm/yyyy" }}</span>
                <i class="pi pi-calendar"></i>
              </button>
              <div v-if="openPicker === 'from'" class="date-picker-panel">
                <div class="date-picker-header">
                  <button type="button" @click="moveCalendarMonth(-1)">
                    <i class="pi pi-chevron-left"></i>
                  </button>
                  <strong>{{ currentMonthLabel }}</strong>
                  <button type="button" @click="moveCalendarMonth(1)">
                    <i class="pi pi-chevron-right"></i>
                  </button>
                </div>
                <div class="date-picker-weekdays">
                  <span v-for="day in weekDays" :key="day">{{ day }}</span>
                </div>
                <div class="date-picker-days">
                  <button
                    v-for="day in calendarDays"
                    :key="day.key"
                    type="button"
                    :class="{ muted: !day.inMonth, selected: day.value === dateFrom, today: day.isToday }"
                    @click="selectDate('from', day.value)"
                  >
                    {{ day.label }}
                  </button>
                </div>
                <div class="date-picker-actions">
                  <button type="button" @click="selectTodayDate('from')">Hôm nay</button>
                  <button type="button" @click="clearDate('from')">Xóa</button>
                </div>
              </div>
            </div>
          </div>
          <div class="flex min-w-0 flex-col gap-1.5">
            <label class="text-sm font-bold text-slate-600">Đến thời gian</label>
            <div class="date-picker">
              <button
                type="button"
                class="date-picker-input"
                @click="toggleDatePicker('to')"
              >
                <span :class="{ 'date-picker-placeholder': !dateTo }">{{ formatDatePickerValue(dateTo) || "dd/mm/yyyy" }}</span>
                <i class="pi pi-calendar"></i>
              </button>
              <div v-if="openPicker === 'to'" class="date-picker-panel">
                <div class="date-picker-header">
                  <button type="button" @click="moveCalendarMonth(-1)">
                    <i class="pi pi-chevron-left"></i>
                  </button>
                  <strong>{{ currentMonthLabel }}</strong>
                  <button type="button" @click="moveCalendarMonth(1)">
                    <i class="pi pi-chevron-right"></i>
                  </button>
                </div>
                <div class="date-picker-weekdays">
                  <span v-for="day in weekDays" :key="day">{{ day }}</span>
                </div>
                <div class="date-picker-days">
                  <button
                    v-for="day in calendarDays"
                    :key="day.key"
                    type="button"
                    :class="{ muted: !day.inMonth, selected: day.value === dateTo, today: day.isToday }"
                    @click="selectDate('to', day.value)"
                  >
                    {{ day.label }}
                  </button>
                </div>
                <div class="date-picker-actions">
                  <button type="button" @click="selectTodayDate('to')">Hôm nay</button>
                  <button type="button" @click="clearDate('to')">Xóa</button>
                </div>
              </div>
            </div>
          </div>
          <div class="flex min-w-0 flex-col gap-1.5">
            <label class="text-sm font-bold text-slate-600">Tìm kiếm</label>
            <span class="relative flex min-w-0">
              <i
                class="pi pi-search pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-sm text-slate-400"
              />
              <InputText
                class="!w-full !pl-10 !pr-3"
                v-model="filters['global'].value"
                placeholder="Tìm theo tiêu đề hoặc người gửi"
              />
            </span>
          </div>
          <div class="flex items-end">
            <Button
              type="button"
              icon="pi pi-filter-slash"
              label="Xoá lọc"
              outlined
              @click="clearFilter()"
            />
          </div>
        </div>
      </div>

      <DataTable
        :value="visibleDocuments"
        :paginator="true"
        stripedRows
        :rows="10"
        :rowsPerPageOptions="[10, 25, 50]"
        :rowHover="true"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        v-model:filters="filters"
        :globalFilterFields="['title', 'sender']"
        currentPageReportTemplate="Có tổng số {totalRecords} văn bản"
        resizableColumns
        tableclass="h-[60px]"
      >
        <Column
          field="title"
          header="Tiêu đề văn bản"
          sortable
          class="h-[60px]"
        ></Column>
        <Column
          field="sender"
          header="Người gửi"
          class="h-[60px]"
          sortable
        ></Column>
        <Column field="time" header="Thời gian" class="h-[60px]" sortable>
          <template #body="slotProps">
            {{ formatDateTime(slotProps.data.time) }}
          </template>
        </Column>
        <Column
          field="replyDeadline"
          header="Hạn trả lời"
          class="h-[60px]"
          sortable
        >
          <template #body="slotProps">
            <span :class="deadlineClass(slotProps.data)">
              {{ formatDeadline(slotProps.data.replyDeadline) }}
            </span>
          </template>
        </Column>
        <Column field="status" header="Trạng thái" class="h-[60px]" sortable>
          <template #body="slotProps">
            <Button
              outlined
              label="Đã xử lý"
              icon="pi pi-check"
              v-if="slotProps.data.status"
              @click="goToViewDocument(slotProps.data.id)"
            />
            <Button
              outlined
              severity="danger"
              label="Chưa xử lý"
              icon="pi pi-hourglass"
              v-else
              @click="goToViewDocument(slotProps.data.id)"
            />
            <!-- Chổ ni bấm là gọi qua api viewdocument/{id} -->
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Confirm Delete Modal -->
  </Panel>
</template>

<script lang="ts">
import { computed, ref } from "vue";
import { useToast } from "@/ui/services/toast";
import router from "@/router";
import { FilterMatchMode } from "@/ui/services/api";
import moment from "moment";
import zaloRepository from "@/services/ZaloRepository";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal.vue";
import { DocumentProcess } from "@/models/documentProcess";

export default {
  components: {
    ConfirmDeleteModal,
  },
  setup() {
    const toast = useToast();
    const listDocumentProcess = ref([] as DocumentProcess[]);
    const loadingBar = ref(false);
    const activeTab = ref<"pending" | "answered">("pending");
    const dateFrom = ref("");
    const dateTo = ref("");
    const openPicker = ref<"from" | "to" | "">("");
    const calendarCursor = ref(moment().startOf("month"));
    const weekDays = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];
    // if(store.state.permission == "admin")
    //
    const loadListDocument = () => {
      loadingBar.value = true;
      zaloRepository
        .getListDocumentProcess()
        .then((response) => {
          listDocumentProcess.value = response.data;
        })
        .catch((err) => {
          toast.add({
            severity: "error",
            summary: "Lỗi",
            detail: err.response?.data || "Lỗi khi tải danh sách văn bản",
            life: 2000,
          });
        })
        .finally(() => {
          loadingBar.value = false;
        });
    };

    loadListDocument();

    const filters = ref({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });

    const clearFilter = () => {
      initFilters();
      dateFrom.value = "";
      dateTo.value = "";
    };

    const initFilters = () => {
      filters.value = {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      };
    };
    const formatDateTime = (date) => {
      return moment(String(date)).format("DD/MM/YYYY");
    };
    const formatDatePickerValue = (date) => {
      if (!date) {
        return "";
      }
      return moment(date).format("DD/MM/YYYY");
    };
    const currentMonthLabel = computed(() =>
      `Tháng ${calendarCursor.value.month() + 1}/${calendarCursor.value.year()}`,
    );
    const calendarDays = computed(() => {
      const startOfMonth = calendarCursor.value.clone().startOf("month");
      const startOffset = (startOfMonth.day() + 6) % 7;
      const firstDay = startOfMonth.clone().subtract(startOffset, "days");
      const today = moment().format("YYYY-MM-DD");

      return Array.from({ length: 42 }, (_, index) => {
        const day = firstDay.clone().add(index, "days");
        return {
          key: day.format("YYYY-MM-DD"),
          value: day.format("YYYY-MM-DD"),
          label: day.date(),
          inMonth: day.month() === calendarCursor.value.month(),
          isToday: day.format("YYYY-MM-DD") === today,
        };
      });
    });
    const toggleDatePicker = (picker: "from" | "to") => {
      openPicker.value = openPicker.value === picker ? "" : picker;
      const selectedDate = picker === "from" ? dateFrom.value : dateTo.value;
      calendarCursor.value = selectedDate
        ? moment(selectedDate).startOf("month")
        : moment().startOf("month");
    };
    const moveCalendarMonth = (step: number) => {
      calendarCursor.value = calendarCursor.value.clone().add(step, "months");
    };
    const selectDate = (picker: "from" | "to", value: string) => {
      if (picker === "from") {
        dateFrom.value = value;
      } else {
        dateTo.value = value;
      }
      openPicker.value = "";
    };
    const selectTodayDate = (picker: "from" | "to") => {
      selectDate(picker, moment().format("YYYY-MM-DD"));
    };
    const clearDate = (picker: "from" | "to") => {
      if (picker === "from") {
        dateFrom.value = "";
      } else {
        dateTo.value = "";
      }
      openPicker.value = "";
    };
    const isInDateRange = (document: DocumentProcess) => {
      const documentDate = moment(document.time);
      if (!documentDate.isValid()) {
        return false;
      }
      if (dateFrom.value && documentDate.isBefore(moment(dateFrom.value).startOf("day"))) {
        return false;
      }
      if (dateTo.value && documentDate.isAfter(moment(dateTo.value).endOf("day"))) {
        return false;
      }
      return true;
    };
    const documentsByTime = computed(() =>
      listDocumentProcess.value.filter(isInDateRange),
    );
    const pendingDocuments = computed(() =>
      documentsByTime.value.filter((item) => !item.status),
    );
    const answeredDocuments = computed(() =>
      documentsByTime.value.filter((item) => item.status),
    );
    const pendingCount = computed(() => pendingDocuments.value.length);
    const answeredCount = computed(() => answeredDocuments.value.length);
    const visibleDocuments = computed(() =>
      activeTab.value === "pending"
        ? pendingDocuments.value
        : answeredDocuments.value,
    );
    const isOverdue = (document: DocumentProcess) => {
      if (document.status || !document.replyDeadline) {
        return false;
      }
      return moment(document.replyDeadline).isBefore(moment());
    };
    const formatDeadline = (date) => {
      if (!date) {
        return "Chưa đặt hạn";
      }
      return moment(String(date)).format("HH:mm DD/MM/YYYY");
    };
    const deadlineClass = (document: DocumentProcess) => {
      if (!document.replyDeadline) {
        return "inline-flex rounded-md bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-600";
      }
      if (isOverdue(document)) {
        return "inline-flex rounded-md bg-rose-50 px-3 py-1.5 text-sm font-semibold text-rose-700";
      }
      return "inline-flex rounded-md bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-700";
    };
    const goToViewDocument = (id: number) => {
      router.push({
        name: "viewDocument",
        params: { id },
      });
    };

    return {
      listDocumentProcess,
      visibleDocuments,
      activeTab,
      dateFrom,
      dateTo,
      openPicker,
      weekDays,
      currentMonthLabel,
      calendarDays,
      toggleDatePicker,
      moveCalendarMonth,
      selectDate,
      selectTodayDate,
      clearDate,
      formatDatePickerValue,
      pendingCount,
      answeredCount,
      filters,
      clearFilter,
      initFilters,
      formatDateTime,
      formatDeadline,
      deadlineClass,
      goToViewDocument,
    };
  },
};
</script>

<style scoped>
.date-picker {
  position: relative;
}

.date-picker-input {
  align-items: center;
  background: #fff;
  border: 1px solid #d8e2ea;
  border-radius: 10px;
  color: #334155;
  display: flex;
  font-size: 15px;
  font-weight: 600;
  gap: 10px;
  height: 42px;
  justify-content: space-between;
  padding: 0 12px;
  width: 100%;
}

.date-picker-input:hover,
.date-picker-input:focus {
  border-color: #7dd3c7;
  box-shadow: 0 0 0 3px rgba(125, 211, 199, 0.14);
  outline: none;
}

.date-picker-placeholder {
  color: #94a3b8;
  font-weight: 500;
}

.date-picker-panel {
  background: #fff;
  border: 1px solid #dbe6ef;
  border-radius: 14px;
  box-shadow: 0 18px 38px rgba(15, 23, 42, 0.12);
  left: 0;
  min-width: 292px;
  padding: 14px;
  position: absolute;
  top: calc(100% + 8px);
  z-index: 50;
}

.date-picker-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}

.date-picker-header strong {
  color: #1e293b;
  font-size: 15px;
  font-weight: 800;
}

.date-picker-header button,
.date-picker-actions button {
  align-items: center;
  border-radius: 8px;
  color: #0f766e;
  display: inline-flex;
  font-weight: 700;
  justify-content: center;
  min-height: 34px;
  padding: 0 10px;
}

.date-picker-header button:hover,
.date-picker-actions button:hover {
  background: #f0fdfa;
}

.date-picker-weekdays,
.date-picker-days {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  text-align: center;
}

.date-picker-weekdays {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 6px;
}

.date-picker-days button {
  align-items: center;
  border-radius: 10px;
  color: #334155;
  display: inline-flex;
  font-size: 14px;
  font-weight: 650;
  height: 36px;
  justify-content: center;
}

.date-picker-days button:hover {
  background: #f0fdfa;
  color: #0f766e;
}

.date-picker-days button.muted {
  color: #94a3b8;
}

.date-picker-days button.today {
  background: #f8fafc;
  box-shadow: inset 0 0 0 1px #7dd3c7;
}

.date-picker-days button.selected {
  background: #ccfbf1;
  color: #0f766e;
  box-shadow: inset 0 0 0 1px #14b8a6;
}

.date-picker-actions {
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  padding-top: 10px;
}

@media (max-width: 640px) {
  .date-picker-panel {
    min-width: min(292px, calc(100vw - 40px));
  }
}
</style>
